import React, { useState } from 'react';
import Loader from '../../components/Loader';
import usePayments from '../../hooks/usePayments';
import { useTranslation } from 'react-i18next';

type ServiceType   = 'sienaguard' | 'report';
type PaymentMethod = 'paypal' | 'card';

interface Payment {
  payment_date:   string;
  service_type:   string;          // llega como string del API
  amount:         number;
  currency:       string;
  payment_method: string;          // idem
  comment:        string;          // nº tokens ó company_name
}

const Payments: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { payments, loading, error } = usePayments();  // ← sin genérico
  const [timeFilter, setTimeFilter] =
    useState<'all' | '3months' | '6months' | '1year'>('all');

  /* -------------- filtro temporal -------------- */
  const filteredPayments = payments.filter(p => {
    if (timeFilter === 'all') return true;

    const now   = new Date();
    const date  = new Date(p.payment_date);

    const limits: Record<'3months' | '6months' | '1year', Date> = {
      '3months': new Date(now.setMonth(now.getMonth() - 3)),
      '6months': new Date(now.setMonth(now.getMonth() - 6)),
      '1year'  : new Date(now.setFullYear(now.getFullYear() - 1))
    };
    return date >= limits[timeFilter];
  });

  if (loading) return <Loader />;

  /* -------------- helpers de traducción -------------- */
  const serviceLabel = (s: string) =>
    t(`payments.service.${s as ServiceType}`, { defaultValue: s });

  const methodLabel  = (m: string) =>
    t(`payments.method.${m as PaymentMethod}`, { defaultValue: m });

  const detailLabel = (p: Payment) => {
    if (p.service_type === 'sienaguard') {
      const count = Number(p.comment) || 0;
      return t('payments.details.tokens', { count });
    }
    return t('payments.details.report', { company: p.comment });
  };

  return (
    <div className="p-8">
      <h1 className="text-4xl font-bold mb-6">{t('payments.title')}</h1>

      {/* ------- selector rango -------- */}
      <div className="mb-6">
        <label htmlFor="timeFilter" className="mr-4 text-xl font-semibold">
          {t('payments.filter_label')}
        </label>

        <select
          id="timeFilter"
          value={timeFilter}
          onChange={e => setTimeFilter(e.target.value as any)}
          className="p-2 border rounded bg-gray-800 text-white font-bold"
        >
          <option value="all">{t('payments.options.all')}</option>
          <option value="3months">{t('payments.options.3months')}</option>
          <option value="6months">{t('payments.options.6months')}</option>
          <option value="1year">{t('payments.options.1year')}</option>
        </select>
      </div>

      {/* ------- tabla pagos -------- */}
      <div className="relative overflow-x-auto bg-white p-4 rounded-lg min-h-[300px]"
           style={{ boxShadow: '0 4px 10px rgba(169,169,169,.6)' }}>
        {filteredPayments.length ? (
          <table className="min-w-full text-left bg-white border rounded-lg">
            <thead className="bg-gray-800 text-white">
              {['date','service','amount','currency','payment_method','details']
               .map(h => (
                 <th key={h} className="py-3 px-6 text-sm font-semibold tracking-wider text-center">
                   {t(`payments.table.header.${h}`)}
                 </th>
               ))}
            </thead>

            <tbody>
              {filteredPayments.map((p, i) => (
                <tr key={i} className="text-center bg-gray-50 even:bg-gray-100 hover:bg-gray-200">
                  <td className="py-3 px-6 border">
                    {new Date(p.payment_date).toLocaleDateString(i18n.language)}
                  </td>
                  <td className="py-3 px-6 border">{serviceLabel(p.service_type)}</td>
                  <td className="py-3 px-6 border">{p.amount.toFixed(2)}</td>
                  <td className="py-3 px-6 border">{p.currency.toUpperCase()}</td>
                  <td className="py-3 px-6 border">{methodLabel(p.payment_method)}</td>
                  <td className="py-3 px-6 border">{detailLabel(p)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="mt-4 text-center text-lg">
            {t('payments.table.no_payments')}
          </p>
        )}

        {error && <p className="text-red-500 mt-4">{error}</p>}
      </div>
    </div>
  );
};

export default Payments;
